import { TileType } from '../types';

export const MAZE_WIDTH = 28;
export const MAZE_HEIGHT = 31;

export const PLAYER_START = { col: 14, row: 22 };
export const CAT_HOUSE_POSITIONS = [
  { col: 12, row: 13 },
  { col: 13, row: 13 },
  { col: 14, row: 13 },
  { col: 15, row: 13 },
];
export const CAT_EXIT = { col: 13, row: 11 };
export const TREAT_SPAWN = { col: 14, row: 16 };

// ---------------------------------------------------------------------------
// Seeded PRNG (mulberry32)
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(arr: T[], rng: () => number): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

// ---------------------------------------------------------------------------
// Union-Find for spanning tree generation
// ---------------------------------------------------------------------------

class UF {
  private p = new Map<string, string>();
  add(k: string) { this.p.set(k, k); }
  find(x: string): string {
    while (this.p.get(x) !== x) {
      const pp = this.p.get(this.p.get(x)!)!;
      this.p.set(x, pp);
      x = pp;
    }
    return x;
  }
  union(a: string, b: string): boolean {
    const ra = this.find(a), rb = this.find(b);
    if (ra === rb) return false;
    this.p.set(ra, rb);
    return true;
  }
}

// ---------------------------------------------------------------------------
// Section generator (works on the LEFT half, cols 1-12)
// ---------------------------------------------------------------------------

interface Edge {
  r1: number; c1: number;
  r2: number; c2: number;
}

const LEFT_COLS = [1, 4, 6, 9, 12];
const TOP_ROWS  = [1, 3, 5, 8];
const BOT_ROWS  = [19, 22, 25, 28];

function nk(r: number, c: number): string { return `${r},${c}`; }

function carveEdge(maze: number[][], e: Edge): void {
  if (e.r1 === e.r2) {
    const lo = Math.min(e.c1, e.c2), hi = Math.max(e.c1, e.c2);
    for (let c = lo; c <= hi; c++) maze[e.r1][c] = TileType.Path;
  } else {
    const lo = Math.min(e.r1, e.r2), hi = Math.max(e.r1, e.r2);
    for (let r = lo; r <= hi; r++) maze[r][e.c1] = TileType.Path;
  }
}

/**
 * Generates a random corridor network for a section of the maze (left half only).
 *
 * 1. Intersection nodes sit at every (row, col) combo.
 * 2. Adjacent intersections are linked by potential corridor edges.
 * 3. Border edges (top/bottom row, left/right col) are mandatory.
 * 4. Interior edges are shuffled; a spanning tree is built (Kruskal's) to
 *    guarantee connectivity, then extra edges are added back for loops.
 */
function generateSection(
  maze: number[][],
  rows: number[],
  rng: () => number,
  loopRate: number,
): void {
  const cols = LEFT_COLS;
  const firstRow = rows[0], lastRow = rows[rows.length - 1];
  const firstCol = cols[0], lastCol = cols[cols.length - 1];

  const edges: Edge[] = [];
  for (let ri = 0; ri < rows.length; ri++) {
    for (let ci = 0; ci < cols.length; ci++) {
      if (ci + 1 < cols.length)
        edges.push({ r1: rows[ri], c1: cols[ci], r2: rows[ri], c2: cols[ci + 1] });
      if (ri + 1 < rows.length)
        edges.push({ r1: rows[ri], c1: cols[ci], r2: rows[ri + 1], c2: cols[ci] });
    }
  }

  const mandatory: Edge[] = [];
  const optional: Edge[] = [];
  for (const e of edges) {
    const h = e.r1 === e.r2, v = e.c1 === e.c2;
    const border =
      (h && (e.r1 === firstRow || e.r1 === lastRow)) ||
      (v && (e.c1 === firstCol || e.c1 === lastCol));
    (border ? mandatory : optional).push(e);
  }

  const uf = new UF();
  for (const r of rows) for (const c of cols) {
    uf.add(nk(r, c));
    maze[r][c] = TileType.Path;
  }

  for (const e of mandatory) {
    uf.union(nk(e.r1, e.c1), nk(e.r2, e.c2));
    carveEdge(maze, e);
  }

  shuffle(optional, rng);
  const extras: Edge[] = [];
  for (const e of optional) {
    if (uf.union(nk(e.r1, e.c1), nk(e.r2, e.c2))) {
      carveEdge(maze, e);
    } else {
      extras.push(e);
    }
  }

  for (const e of extras) {
    if (rng() < loopRate) carveEdge(maze, e);
  }
}

// ---------------------------------------------------------------------------
// Fixed middle section (rows 9-18): cat house, tunnels, surrounding corridors
// ---------------------------------------------------------------------------

function stampMiddleSection(maze: number[][]): void {
  // Vertical corridors at cols 6, 21 (full middle height)
  for (let r = 9; r <= 18; r++) {
    maze[r][6] = TileType.Path;
    maze[r][21] = TileType.Path;
  }

  // Short vertical corridors at cols 12, 15 (rows 9-11 only)
  for (let r = 9; r <= 11; r++) {
    maze[r][12] = TileType.Path;
    maze[r][15] = TileType.Path;
  }

  // Horizontal corridor above cat house (row 11, cols 9-18)
  for (let c = 9; c <= 18; c++) maze[11][c] = TileType.Path;

  // Horizontal corridor below cat house (row 16, cols 9-18)
  for (let c = 9; c <= 18; c++) maze[16][c] = TileType.Path;

  // Cat house top wall (row 12, cols 10-17) with door at 13-14
  for (let c = 10; c <= 17; c++) maze[12][c] = TileType.Wall;
  maze[12][9]  = TileType.Path;
  maze[12][18] = TileType.Path;
  maze[12][13] = TileType.CatDoor;
  maze[12][14] = TileType.CatDoor;

  // Cat house side walls (cols 10, 17) and interior (cols 11-16)
  for (let r = 13; r <= 14; r++) {
    maze[r][10] = TileType.Wall;
    maze[r][17] = TileType.Wall;
    for (let c = 11; c <= 16; c++) maze[r][c] = TileType.Empty;
  }

  // Cat house bottom wall (row 15, cols 10-17)
  for (let c = 10; c <= 17; c++) maze[15][c] = TileType.Wall;
  maze[15][9]  = TileType.Path;
  maze[15][18] = TileType.Path;

  // Vertical corridors flanking cat house (cols 9, 18)
  for (let r = 11; r <= 18; r++) {
    maze[r][9]  = TileType.Path;
    maze[r][18] = TileType.Path;
  }

  // Tunnel row 13 — paths on both sides and through to cat house walls
  for (let c = 0; c <= 9; c++)  maze[13][c] = TileType.Path;
  for (let c = 18; c <= 27; c++) maze[13][c] = TileType.Path;

  // Re-stamp cat house interior on row 13 (tunnel carving overwrote it)
  maze[13][10] = TileType.Wall;
  maze[13][17] = TileType.Wall;
  for (let c = 11; c <= 16; c++) maze[13][c] = TileType.Empty;
}

// ---------------------------------------------------------------------------
// Mirror left half (cols 0-13) to right half (cols 14-27)
// ---------------------------------------------------------------------------

function mirror(maze: number[][]): void {
  for (let r = 0; r < MAZE_HEIGHT; r++) {
    for (let c = 0; c <= 13; c++) {
      maze[r][27 - c] = maze[r][c];
    }
  }
}

// ---------------------------------------------------------------------------
// Connect top/bottom sections to the fixed middle section
// ---------------------------------------------------------------------------

function connectSections(maze: number[][]): void {
  // Top row 8 → middle row 9 at cols 6, 12 (and mirrors 21, 15)
  for (const c of [6, 12, 15, 21]) {
    maze[8][c]  = TileType.Path;
    maze[9][c]  = TileType.Path;
  }

  // Middle row 18 → bottom row 19 at cols 6, 9 (and mirrors 21, 18)
  for (const c of [6, 9, 18, 21]) {
    maze[18][c] = TileType.Path;
    maze[19][c] = TileType.Path;
  }

  // Ensure player start area (row 22, cols 12-15) is open
  for (let c = 12; c <= 15; c++) maze[22][c] = TileType.Path;
}

// ---------------------------------------------------------------------------
// Flood-fill connectivity check — carve rescue paths for any isolated tiles
// ---------------------------------------------------------------------------

function ensureConnectivity(maze: number[][]): void {
  const visited: boolean[][] = Array.from(
    { length: MAZE_HEIGHT },
    () => Array(MAZE_WIDTH).fill(false),
  );
  const walkable = (r: number, c: number) =>
    r >= 0 && r < MAZE_HEIGHT && c >= 0 && c < MAZE_WIDTH &&
    maze[r][c] !== TileType.Wall;

  const queue: [number, number][] = [[PLAYER_START.row, PLAYER_START.col]];
  visited[PLAYER_START.row][PLAYER_START.col] = true;

  while (queue.length > 0) {
    const [r, c] = queue.shift()!;
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      let nr = r + dr, nc = c + dc;
      if (nc < 0) nc = MAZE_WIDTH - 1;
      else if (nc >= MAZE_WIDTH) nc = 0;
      if (nr < 0 || nr >= MAZE_HEIGHT) continue;
      if (walkable(nr, nc) && !visited[nr][nc]) {
        visited[nr][nc] = true;
        queue.push([nr, nc]);
      }
    }
  }

  // Rescue any unreachable path tile by carving toward the nearest visited tile
  for (let r = 1; r < MAZE_HEIGHT - 1; r++) {
    for (let c = 1; c < MAZE_WIDTH - 1; c++) {
      if (walkable(r, c) && !visited[r][c] &&
          maze[r][c] !== TileType.Empty && maze[r][c] !== TileType.CatDoor) {
        // Carve rightward until hitting a visited tile
        for (let cc = c + 1; cc < MAZE_WIDTH - 1; cc++) {
          maze[r][cc] = TileType.Path;
          if (visited[r][cc]) break;
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Cheese placement
// ---------------------------------------------------------------------------

function placeCheese(maze: number[][]): void {
  for (let r = 0; r < MAZE_HEIGHT; r++) {
    for (let c = 0; c < MAZE_WIDTH; c++) {
      if (maze[r][c] !== TileType.Path) continue;

      // No cheese in the cat-house zone (rows 9-18 between cols 7-20)
      if (r >= 9 && r <= 18 && c >= 7 && c <= 20) continue;

      // No cheese on player start tile
      if (r === PLAYER_START.row && c === PLAYER_START.col) continue;

      maze[r][c] = TileType.Cheese;
    }
  }

  // Power cheese near the four corners
  const pwr = [
    { r: 3, c: 1 },  { r: 3, c: 26 },
    { r: 25, c: 1 }, { r: 25, c: 26 },
  ];
  for (const p of pwr) {
    const t = maze[p.r][p.c];
    if (t === TileType.Cheese || t === TileType.Path) {
      maze[p.r][p.c] = TileType.PowerCheese;
    }
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getMazeForLevel(level: number): number[][] {
  const rng = mulberry32(level * 2654435761);

  const maze: number[][] = Array.from(
    { length: MAZE_HEIGHT },
    () => Array(MAZE_WIDTH).fill(TileType.Wall),
  );

  // Generate left-half corridors for top and bottom playable zones
  generateSection(maze, TOP_ROWS, rng, 0.55 + rng() * 0.25);
  generateSection(maze, BOT_ROWS, rng, 0.55 + rng() * 0.25);

  // Mirror left half → right half
  mirror(maze);

  // Stamp the fixed cat-house / tunnel zone (overwrites rows 9-18)
  stampMiddleSection(maze);

  // Vertical stitching between zones
  connectSections(maze);

  // Guarantee every walkable tile is reachable
  ensureConnectivity(maze);

  // Populate with cheese
  placeCheese(maze);

  return maze;
}

export function countCheese(maze: number[][]): number {
  let count = 0;
  for (const row of maze) {
    for (const tile of row) {
      if (tile === TileType.Cheese || tile === TileType.PowerCheese) count++;
    }
  }
  return count;
}
